import React, { useState, useEffect, useCallback } from 'react';
import { useDrawingStore } from '../../../store/useDrawingStore';

const MIN_PERCENT = 10;
const MAX_PERCENT = 400;
const STEP = 10;

export const ZoomControl: React.FC<{ inline?: boolean }> = ({ inline = false }) => {
  // drawing store holds viewTransform.scale where 1.0 == 100%
  const scale = useDrawingStore((s) => s.viewTransform.scale);
  const setZoomUnified = useDrawingStore((s) => s.setZoomUnified);

  const [percent, setPercent] = useState(() => Math.round((scale || 1) * 100));

  // clamp helper
  const clampPercent = useCallback((p: number) => {
    return Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, p));
  }, []);

  // keep local percent in sync when store changes externally
  useEffect(() => {
    const p = Math.round((scale || 1) * 100);
    setPercent(p);
  }, [scale]);

  const applyPercentToStore = useCallback((p: number) => {
    const clamped = clampPercent(p);
    setPercent(clamped);
    setZoomUnified(clamped / 100, { origin: 'center' });
  }, [clampPercent, setZoomUnified]);

  const onMinus = () => {
    // step down
    const next = clampPercent(percent - STEP);
    applyPercentToStore(next);
  };

  const onPlus = () => {
    const next = clampPercent(percent + STEP);
    applyPercentToStore(next);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // allow empty input while typing
    const v = e.target.value.replace(/[^0-9]/g, '');
    if (v === '') {
      setPercent(NaN as unknown as number);
      return;
    }
    const n = parseInt(v, 10);
    setPercent(n);
  };

  const onBlur = () => {
    if (isNaN(percent)) {
      // revert to store
      setPercent(Math.round((scale || 1) * 100));
    } else {
      applyPercentToStore(percent);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div style={ inline ? { display: 'flex', alignItems: 'center' } : { position: 'absolute', bottom: 12, right: 12, zIndex: 60 }}>
      <div className={`flex items-center space-x-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded px-2 py-1 shadow ${inline ? '' : ''}`}>
        <button
          onClick={onMinus}
          className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded"
          aria-label="Zoom out"
        >
          -
        </button>
        <input
          aria-label="Zoom percent"
          value={isNaN(percent) ? '' : percent}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className="w-16 text-center bg-transparent outline-none text-sm"
        />
        <span className="text-sm">%</span>
        <button
          onClick={onPlus}
          className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded"
          aria-label="Zoom in"
        >
          +
        </button>
      </div>
    </div>
  );
};

export default ZoomControl;
